"use client"

import * as React from "react"
import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Tip } from "@/components/ui/tip"
import { cn } from "@/lib/utils"

export function LeoCardStack({
  count,
  index,
  label,
  children,
  className,
}: {
  count: number
  index: number
  label: string
  children: ReactNode
  className?: string
}) {
  const stacked = count > 1
  // Cards still ahead of this one. Going forward shows fewer. Going back shows more.
  // Visited cards do not add a second layer, so the front card stays the same size.
  const behindCount = Math.min(Math.max(0, count - index - 1), 2)
  const frontRef = React.useRef<HTMLLIElement>(null)
  const previousIndexRef = React.useRef(index)

  React.useLayoutEffect(() => {
    const previousIndex = previousIndexRef.current
    previousIndexRef.current = index
    if (previousIndex === index || !stacked) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return
    const front = frontRef.current
    if (!front?.animate) return
    const direction = index > previousIndex ? 1 : -1
    const animation = front.animate(
      [
        { opacity: 0.72, transform: `translateX(${direction * 18}px) scale(0.985)` },
        { opacity: 1, transform: "translateX(0) scale(1)" },
      ],
      { duration: 220, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
    )
    return () => animation.cancel()
  }, [index, stacked])

  return (
    <div className={cn(
      "min-w-0 overflow-visible p-1",
      behindCount === 1 && "pb-3",
      behindCount === 2 && "pb-5",
    )}>
      <ul className="list-none relative isolate min-w-0" aria-label={stacked ? `${label}, ${index + 1} of ${count}` : label}>
        {Array.from({ length: behindCount }, (_, depth) => (
          <div
            key={`after-${depth}`}
            aria-hidden="true"
            data-slot="leo-card-stack-peek"
            data-side="behind"
            className={cn(
              "pointer-events-none absolute rounded-xl border border-border",
              depth === 0
                ? "inset-x-2 top-1 -bottom-3 z-[1] bg-card shadow-sm"
                : "inset-x-4 top-2 -bottom-6 z-0 bg-muted/60 shadow-sm",
            )}
          />
        ))}
        <li className="list-none relative z-[3] min-w-0" ref={frontRef}>
          <Card size="sm" className={cn("gap-2 overflow-visible shadow-lg", className)}>
            {children}
          </Card>
        </li>
      </ul>
    </div>
  )
}

export function LeoCardStackNavigation({
  count,
  index,
  onChange,
  noun,
  showCount = true,
}: {
  count: number
  index: number
  onChange: (index: number) => void
  noun: string
  /**
   * Print "2 of 5" between the chevrons.
   *
   * Off on narrow rails, where the counter competes with the card's own
   * heading for a row it cannot both fit on. The stack already shows how
   * much is left — that is what the peek layers behind the front card are
   * for — and the position stays in the accessible name either way.
   */
  showCount?: boolean
}) {
  if (count < 2) return null
  const position = `${noun}, ${index + 1} of ${count}`
  return (
    <div className="ms-auto flex shrink-0 items-center gap-0.5">
      <Tip label={`Previous ${noun}`}>
        <Button type="button" variant="ghost" size="icon-sm" className="size-8" disabled={index === 0} aria-label={`Previous ${noun}`} onClick={() => onChange(index - 1)}>
          <i className="fa-light fa-chevron-left" aria-hidden="true" />
        </Button>
      </Tip>
      {showCount ? (
        <span className="min-w-8 text-center text-xs tabular-nums text-muted-foreground" aria-live="polite">{index + 1} of {count}</span>
      ) : (
        <span className="sr-only" aria-live="polite">{position}</span>
      )}
      <Tip label={`Next ${noun}`}>
        <Button type="button" variant="ghost" size="icon-sm" className="size-8" disabled={index >= count - 1} aria-label={`Next ${noun}`} onClick={() => onChange(index + 1)}>
          <i className="fa-light fa-chevron-right" aria-hidden="true" />
        </Button>
      </Tip>
    </div>
  )
}
